import { Dispatch } from "react";
import { AxiosResponse } from "axios";

import {
  showAlert,
  isAPISuccessful,
  checkInternetAndShowAlert,
} from "../../../common/utils";
import {
  Env,
  Module,
  EndPoint,
  getApiCall,
  patchApiCall,
  postApiCall,
} from "../../../common/constants";
import { setUserData } from "./slice";
import { UpdateProfileFormValues } from "./type";
import { setLoading } from "../../../redux/global.slice";
import { $axios } from "../../../common/constants/axios.instance";

const RESTRICTED_USERNAME_MESSAGE = `This username is unavailable. Please choose another. If you believe this is a mistake, reach out to ${Env.SUPPORT_EMAIL} with the subject line 'Unavailable Username' and be sure to include your phone number!`;

const RESERVED_USERNAME_MESSAGE = `This username is unavailable or has been reserved. If you believe this is a mistake, reach out to ${Env.SUPPORT_EMAIL} with the subject line 'Username Change Request' and be sure to include your phone number!`;

const getUsernameValidationMessage = (
  isUsernameExists?: boolean,
  isRestrictedUsername?: boolean,
  isReservedUsername?: boolean,
) => {
  const hasRestriction = Boolean(isRestrictedUsername);
  const hasReservation = Boolean(isReservedUsername);
  const usernameExistsOnly =
    !hasRestriction && !hasReservation && Boolean(isUsernameExists);

  if (hasRestriction) return RESTRICTED_USERNAME_MESSAGE;
  if (hasReservation) return RESERVED_USERNAME_MESSAGE;
  if (usernameExistsOnly) {
    return "This username is unavailable. Please choose another.";
  }
  return "";
};

export const getUserProfile =
  (showLoader = true, callback?: (data?: any) => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    if (showLoader) dispatch(setLoading(true));

    getApiCall(
      Module.consumer + EndPoint.USER_PROFILE,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data.statusCode)) {
          dispatch(setUserData(data.data));
          if (callback) callback(data.data);
        } else if (data.error) showAlert(2, data.message);
        if (showLoader) dispatch(setLoading(false));
      },
      (error: any) => {
        if (showLoader) dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const checkUsername =
  (
    username: string,
    errorCallback: (isValid: boolean, message?: string) => void,
    successCallback: () => void,
  ) =>
  async (dispatch: Dispatch<any>) => {
    //  Check for internet connection
    if (!checkInternetAndShowAlert(showAlert)) return;

    const dataToSend = { username };

    try {
      const response: AxiosResponse<any> = await $axios.post(
        `${Module.consumer}${EndPoint.VERIFY_USERNAME}`,
        dataToSend,
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
          },
        },
      );
      const { data } = response;

      if (isAPISuccessful(data.statusCode)) {
        // Interpret the inner validation data
        const { isUsernameExists, isRestrictedUsername, isReservedUsername } =
          data?.data || {};

        const validationMessage = getUsernameValidationMessage(
          isUsernameExists,
          isRestrictedUsername,
          isReservedUsername,
        );

        if (validationMessage) {
          errorCallback(false, validationMessage);
          dispatch(setLoading(false));
          return;
        }

        successCallback();
      }
    } catch (error: any) {
      // 🔹 Handle 401 or network errors gracefully
      if (error?.response?.status === 401) {
        showAlert(2, "Session expired. Please login again.");
        window.location.href = "/login";
      } else {
        showAlert(2, error?.message || "Something went wrong");
      }
      dispatch(setLoading(false));
    }
  };

export const updateProfile =
  (values: UpdateProfileFormValues | { name: string }, callback: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    patchApiCall(
      Module.consumer + EndPoint.USER_PROFILE,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;

        if (isAPISuccessful(data.statusCode)) {
          callback();
          showAlert(1, data.message);
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };

export const userLogout =
  (callback: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.consumer + EndPoint.USER_LOGOUT,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;

        if (isAPISuccessful(data.statusCode)) {
          callback();
          showAlert(1, data.message);
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);

        dispatch(setLoading(false));
      },
    );
  };
